/**
 * Internal Payment Services Contracts
 *
 * DATABASE-FIRST: Schemas match PostgreSQL exactly
 * Source: MCP postgres queries on 2026-01-02
 *
 * Table: payment_services
 * Columns: id, hash (unique), name, type, created_at, turned_off, restarted
 * CHECK constraint valid_type: deposit, withdrawal
 *
 * Used by:
 * - payment-service → backend (get available providers, cascade config)
 * - admin panel → backend (manage providers)
 */
import { z } from 'zod';
import { paymentServiceTypeSchema } from './cascade.js';
// =============================================================================
// PAYMENT SERVICE ENTITY
// Table: payment_services
// =============================================================================
/**
 * Payment service info - matches repository PaymentServiceInfo shape
 * Note: This is what the repository returns, NOT the raw DB entity
 */
export const paymentServiceInfoSchema = z.object({
    id: z.number(),
    hash: z.string(), // Stable identifier (e.g., 'fservice')
    name: z.string(), // Display name (e.g., 'FastPay')
    type: paymentServiceTypeSchema, // 'deposit' or 'withdrawal'
    active: z.boolean(),
    minAmount: z.number(),
    maxAmount: z.number(),
});
// =============================================================================
// GET PAYMENT SERVICE BY HASH
// Endpoint: GET /api/internal/payment-services/:hash
// =============================================================================
/**
 * Get payment service by hash - success response
 */
export const getPaymentServiceByHashResponseSchema = z.object({
    success: z.literal(true),
    data: paymentServiceInfoSchema,
});
/**
 * Get payment service by hash - not found response
 */
export const getPaymentServiceNotFoundResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});
// =============================================================================
// GET PAYMENT SERVICES LIST
// Endpoint: GET /api/internal/payment-services
// Query params: ?type=deposit|withdrawal (optional filter)
// =============================================================================
/**
 * Get payment services list query params
 */
export const getPaymentServicesQuerySchema = z.object({
    type: paymentServiceTypeSchema.optional(),
});
/**
 * Get payment services list response
 */
export const getPaymentServicesResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        services: z.array(paymentServiceInfoSchema),
    }),
});
/**
 * Get payment services list error response
 */
export const getPaymentServicesErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});
// =============================================================================
// GET CASCADE INFO
// Endpoint: GET /api/internal/cascade/info
// Query params: ?amount=1000&type=deposit
// Returns available methods for the given amount
// =============================================================================
/**
 * Get cascade info query params
 */
export const getCascadeInfoQuerySchema = z.object({
    amount: z.coerce.number().positive(),
    type: paymentServiceTypeSchema,
});
/**
 * Cascade method info - returned from get_cascade_info_new()
 */
export const cascadeMethodInfoSchema = z.object({
    hash: z.string(),
    name: z.string(),
    position: z.number(),
    minAmount: z.number(),
    maxAmount: z.number().nullable(),
});
/**
 * Get cascade info response
 */
export const getCascadeInfoResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        amount: z.number(),
        type: paymentServiceTypeSchema,
        methods: z.array(cascadeMethodInfoSchema),
    }),
});
/**
 * Get cascade info error response
 */
export const getCascadeInfoErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});
// =============================================================================
// GET ACTIVE CASCADE
// Endpoint: GET /api/internal/cascade/active
// Returns current active cascade with positions
// =============================================================================
/**
 * Active cascade data - matches repository CascadeInfo shape
 * Note: Repository transforms DB data into this shape
 */
export const activeCascadeDataSchema = z.object({
    id: z.number(),
    deposits: z.array(z.string()), // Array of provider hashes
    withdrawals: z.array(z.string()), // Array of provider hashes
    startedAt: z.date(), // Repository returns Date object
    endedAt: z.date().nullable(),
}).nullable();
/**
 * Cascade positions data - matches repository CascadePositions shape
 */
export const cascadePositionsDataSchema = z.object({
    cascadeId: z.number(),
    depositPositions: z.record(z.number()), // { hash: position }
    withdrawalPositions: z.record(z.number()), // { hash: position }
    createdAt: z.date(),
    staffId: z.string().nullable(),
}).nullable();
/**
 * Get active cascade response
 */
export const getActiveCascadeResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        cascade: activeCascadeDataSchema,
        positions: cascadePositionsDataSchema,
    }),
});
/**
 * Get active cascade error response
 */
export const getActiveCascadeErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});
//# sourceMappingURL=payment-services.js.map